import { useEffect, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { ROLE_LABELS } from "@easyshop/shared";
import { Avatar, Button, Card, ErrorState, Pill, SectionTitle, cx, inputClass } from "@/components/ui";
import { useSession } from "@/hooks/useSession";
import { api, setCurrentOrg } from "@/lib/api";
import { queryClient } from "@/lib/query-client";
import { disablePush, enablePush, pushSupported } from "@/lib/pwa";
import { initials } from "@/lib/format";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const isIos = () => /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;

/** Promesse bornée : le service worker peut ne jamais être prêt (ex. en développement). */
function withTimeout<T>(p: Promise<T>, ms: number, message: string): Promise<T> {
  return Promise.race([p, new Promise<T>((_, reject) => setTimeout(() => reject(new Error(message)), ms))]);
}

function Note({ tone = "info", children }: { tone?: "info" | "ok" | "error"; children: ReactNode }) {
  return (
    <div
      className={cx(
        "mt-3 rounded-card-sm px-4 py-3 text-[12.5px] font-semibold leading-[1.45]",
        tone === "ok" ? "bg-positive-bg text-positive" : tone === "error" ? "bg-danger-bg text-danger" : "bg-page text-muted",
      )}
    >
      {children}
    </div>
  );
}

async function switchTo(orgId: string) {
  await api.post("/me/organization", { organizationId: orgId });
  // L'en-tête x-organization-id prime sur le réseau par défaut : on le bascule avant de tout recharger.
  setCurrentOrg(orgId);
  await queryClient.invalidateQueries();
}

function PushSection() {
  const supported = pushSupported();
  const [subscribed, setSubscribed] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ tone: "info" | "ok" | "error"; text: string } | null>(null);

  useEffect(() => {
    if (!supported) return;
    let alive = true;
    withTimeout(navigator.serviceWorker.ready, 3000, "sw")
      .then((reg) => reg.pushManager.getSubscription())
      .then((s) => alive && setSubscribed(Boolean(s) && Notification.permission === "granted"))
      .catch(() => alive && setSubscribed(false));
    return () => {
      alive = false;
    };
  }, [supported]);

  const toggle = async () => {
    setBusy(true);
    setMsg(null);
    try {
      if (subscribed) {
        await withTimeout(disablePush(), 10_000, "Le service worker ne répond pas.");
        setSubscribed(false);
        setMsg({ tone: "info", text: "Notifications désactivées sur cet appareil." });
      } else {
        const r = await withTimeout(enablePush(), 20_000, "Le service worker ne répond pas : rechargez la page puis réessayez.");
        if (r === "granted") {
          setSubscribed(true);
          setMsg({ tone: "ok", text: "Notifications activées sur cet appareil." });
        } else if (r === "denied") {
          setMsg({
            tone: "error",
            text: "Autorisation refusée. Réactivez les notifications pour Pilote dans les réglages du navigateur.",
          });
        } else if (r === "unavailable") {
          setMsg({ tone: "error", text: "Indisponible : le serveur n'a pas encore de clés de notification (VAPID) configurées." });
        } else {
          setMsg({ tone: "error", text: "Ce navigateur ne prend pas en charge les notifications push." });
        }
      }
    } catch (e) {
      setMsg({ tone: "error", text: e instanceof Error ? e.message : "Opération impossible" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-1">
          <SectionTitle>Notifications sur cet appareil</SectionTitle>
          <div className="mt-1 text-[12.5px] leading-[1.5] text-muted">
            Nouveaux messages, tâches et alertes de stock, même application fermée.
            {isIos() &&
              !isStandalone() &&
              " Sur iPhone / iPad, installez d'abord l'application sur l'écran d'accueil, puis ouvrez-la depuis l'icône."}
          </div>
        </div>
        {supported && (
          <Button variant={subscribed ? "outline" : "primary"} onClick={() => void toggle()} disabled={busy || subscribed === null}>
            {busy ? "…" : subscribed ? "Désactiver" : "Activer les notifications"}
          </Button>
        )}
      </div>
      {!supported && (
        <Note>
          {isIos()
            ? "Sur iPhone / iPad, les notifications ne sont disponibles qu'une fois l'application installée sur l'écran d'accueil (iOS 16.4 ou plus)."
            : "Ce navigateur ne prend pas en charge les notifications push."}
        </Note>
      )}
      {supported && subscribed && !msg && <Note tone="ok">Activées sur cet appareil.</Note>}
      {msg && <Note tone={msg.tone}>{msg.text}</Note>}
    </Card>
  );
}

function InstallSection() {
  const [prompt, setPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(isStandalone());
  useEffect(() => {
    const onPrompt = (e: Event) => {
      e.preventDefault();
      setPrompt(e as BeforeInstallPromptEvent);
    };
    const onInstalled = () => setInstalled(true);
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  return (
    <Card>
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-1">
          <SectionTitle>Installer l'application</SectionTitle>
          <div className="mt-1 text-[12.5px] leading-[1.5] text-muted">
            {installed
              ? "Pilote est installée sur cet appareil."
              : isIos()
                ? "Dans Safari, touchez Partager → « Sur l'écran d'accueil »."
                : prompt
                  ? "Ajoutez Pilote à votre écran d'accueil ou à votre bureau, comme une application."
                  : "Dans le menu du navigateur, choisissez « Installer l'application » ou « Ajouter à l'écran d'accueil »."}
          </div>
        </div>
        {!installed && prompt && (
          <Button
            onClick={() =>
              void prompt.prompt().then(() =>
                prompt.userChoice.then((c) => {
                  if (c.outcome === "accepted") setInstalled(true);
                  setPrompt(null);
                }),
              )
            }
          >
            Installer
          </Button>
        )}
      </div>
    </Card>
  );
}

function NetworksSection() {
  const { me } = useSession();
  const navigate = useNavigate();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [name, setName] = useState("");

  const run = async (key: string, fn: () => Promise<void>) => {
    setBusy(key);
    setError(null);
    try {
      await fn();
      navigate("/");
    } catch (e) {
      setError(e);
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card>
      <div id="reseaux" className="scroll-mt-24">
        <SectionTitle>Réseaux</SectionTitle>
        <div className="mt-1 text-[12.5px] text-muted">
          Les réseaux auxquels vous avez accès. Le réseau actif s'applique à tout Pilote.
        </div>
      </div>
      <div className="mt-3">
        {me.organizations.map((o) => {
          const current = o.id === me.organization?.id;
          return (
            <div key={o.id} className="flex items-center gap-3 border-t border-line-soft py-3 first:border-t-0">
              <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-[11px] bg-accent-soft font-display text-[13px] font-extrabold text-accent">
                {initials(o.name)}
              </div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-[13.5px] font-semibold">{o.name}</div>
                <div className="text-[11.5px] text-muted-2">{ROLE_LABELS[o.role]}</div>
              </div>
              {current ? (
                <Pill className="bg-positive-bg text-positive">Actif</Pill>
              ) : (
                <button
                  type="button"
                  disabled={busy != null}
                  onClick={() => void run(o.id, () => switchTo(o.id))}
                  className="rounded-full border border-line px-[14px] py-[6px] text-[12px] font-bold text-accent hover:bg-page disabled:opacity-50"
                >
                  {busy === o.id ? "…" : "Basculer"}
                </button>
              )}
            </div>
          );
        })}
      </div>
      {me.platformAdmin && (
      <div className="mt-2 border-t border-line-soft pt-4">
        <div className="text-[12.5px] font-bold">Créer un nouveau réseau</div>
        <div className="mt-1 text-[11.5px] text-muted-2">
          Vous en serez propriétaire ; vous pourrez ensuite y connecter vos comptes Hiboutik.
        </div>
        <form
          className="mt-2 flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            const n = name.trim();
            if (!n) return;
            void run("create", async () => {
              const org = await api.post<{ id: string }>("/organizations", { name: n });
              setCurrentOrg(org.id);
              setName("");
              await queryClient.invalidateQueries();
            });
          }}
        >
          <input className={inputClass} placeholder="Nom du réseau" value={name} onChange={(e) => setName(e.target.value)} />
          <Button type="submit" disabled={!name.trim() || busy != null}>
            {busy === "create" ? "Création…" : "Créer"}
          </Button>
        </form>
      </div>
      )}
      {error != null && <ErrorState error={error} className="mt-3" />}
    </Card>
  );
}

export function ProfilePage() {
  const { me } = useSession();

  useEffect(() => {
    if (window.location.hash === "#reseaux") document.getElementById("reseaux")?.scrollIntoView();
  }, []);

  return (
    <div className="mx-auto flex max-w-[760px] flex-col gap-[18px]">
      <Card>
        <div className="flex flex-wrap items-center gap-4">
          <Avatar text={initials(me.user.name)} className="h-14 w-14 text-[18px]" />
          <div className="min-w-0 flex-1">
            <div className="truncate font-display text-[20px] font-bold">{me.user.name}</div>
            <div className="truncate text-[13px] text-muted">{me.user.email}</div>
            {me.role && (
              <div className="mt-2 flex flex-wrap items-center gap-2 text-[12px] text-muted-2">
                <Pill className="bg-accent-soft text-accent">{ROLE_LABELS[me.role]}</Pill>
                <span>
                  {me.organization?.name}
                  {me.networkWide ? ` · tout le réseau (${me.networkSize} boutiques)` : ` · ${me.boutiques.map((b) => b.city).join(", ")}`}
                </span>
              </div>
            )}
          </div>
        </div>
      </Card>

      <NetworksSection />
      <PushSection />
      <InstallSection />
    </div>
  );
}
